import { Check, Hash, Plus } from "lucide-react-native";
import { memo, useCallback, useMemo } from "react";

import { type Tag } from "@/features/player-kanban/types/types";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/*
 * design-system: dynamic-style waiver — data-driven tag colour swatch (no
 * semantic token); passed as a single style reference so no inline object
 * literal renders on a JSX element.
 */
const swatchStyle = (color: string) => ({ backgroundColor: color });

/**
 * Row chrome. The menu-row idiom the board's dropdowns use: a full-width press
 * target, radius 4, a raised surface on hover, and a selected row that reads
 * as selected through its fill plus a checked box — not a saturated blue pill.
 */
const ROW_BASE_CLASS =
  "web:transition-colors web:hover:bg-surface-raised flex-row items-center gap-3 rounded-sm px-3 py-2";
const ROW_SELECTED_CLASS = "bg-surface-raised";

/**
 * Checkbox chrome, converged on the tag-picker sheet (Figma 44:171095): 20px
 * square, radius 4, 2px border.
 *
 * Two deliberate deviations from that node, both flagged:
 *
 *  - The sheet's unchecked border samples #A5A3A3 / #8C8D8D, which sits between
 *    this system's `outline-strong` (211,211,211 / 115,116,116) and the next
 *    shade down. `outline-strong` is the nearest CLOSED token; the exact value
 *    has none.
 *  - The sheet fills the CHECKED box accent-blue in light but WHITE in dark.
 *    This uses `accent-primary` in both, because every other selected state in
 *    the app reads accent-on-both-themes, and a white-filled checkbox would be
 *    the only one of its kind. The same file's CTA variable was already proven
 *    to disagree with its own render, so its dark samples are weak evidence for
 *    inverting an established pattern.
 */
const CHECKBOX_BASE_CLASS =
  "size-5 shrink-0 items-center justify-center rounded-sm border-2";
const CHECKBOX_CHECKED_CLASS = "border-accent-primary bg-accent-primary";
const CHECKBOX_UNCHECKED_CLASS = "border-outline-strong bg-transparent";

/**
 * Props for TagListItem component
 */
interface TagListItemProps {
  item: Tag & { color: string };
  isSelected: boolean;
  onToggleTag: (tag: Tag) => void;
}

/**
 * Individual tag list item component
 * @param props - Component props
 * @param props.item - Tag data
 * @param props.isSelected - Whether tag is selected
 * @param props.onToggleTag - Toggle callback
 */
const TagListItem = memo(function TagListItem({
  item,
  isSelected,
  onToggleTag,
}: TagListItemProps) {
  const handlePress = useCallback(() => {
    onToggleTag(item);
  }, [onToggleTag, item]);

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: isSelected }}
      accessibilityLabel={`${isSelected ? "Remove" : "Add"} ${item.name} tag`}
      className={`${ROW_BASE_CLASS} ${isSelected ? ROW_SELECTED_CLASS : ""}`}
    >
      <Box
        className={`${CHECKBOX_BASE_CLASS} ${
          isSelected ? CHECKBOX_CHECKED_CLASS : CHECKBOX_UNCHECKED_CLASS
        }`}
      >
        {isSelected && (
          <Icon as={Check} size="xs" className="text-content-on-accent" />
        )}
      </Box>
      {/* design-system: dynamic-style waiver — data-driven value (tag color) */}
      <Box
        className="size-3 shrink-0 rounded-full"
        // eslint-disable-next-line no-restricted-syntax -- data-driven tag swatch color (arbitrary item.color hex); no closed color token
        UNSAFE_style={swatchStyle(item.color)}
      />
      {/* Checked rows read at full contrast, unchecked ones a step back. The
          sheet's unchecked label is typography-600, which has no semantic
          entry; `content-secondary` (700) is the nearest and stays legible
          where `content-muted` (500) would read as disabled. */}
      <Text
        variant="unstyled"
        className={`min-w-0 flex-1 truncate ${
          isSelected ? "text-content-primary" : "text-content-secondary"
        }`}
        numberOfLines={1}
      >
        {item.name}
      </Text>
    </Pressable>
  );
});

/**
 * Props for TagSelectorAvailableTags component
 */
interface TagSelectorAvailableTagsProps {
  searchQuery: string;
  filteredTags: (Tag & { color: string })[];
  playerTagIds: readonly string[];
  isCreating: boolean;
  canCreateNew: boolean;
  onToggleTag: (tag: Tag) => void;
  onCreateFromSearch: () => void;
}

/**
 * TagSelectorAvailableTags View Component
 * Pure UI component for the available-tags list and the inline create offer.
 *
 * The list is a bounded `ScrollView` rather than a `FlashList`: the tag query
 * caps at 100 rows and the modal body is itself a ScrollView, so a nested
 * virtualized list was both unnecessary and a nesting hazard.
 *
 * The separate `Quick Create:` box is gone. Typing a name that matches no
 * existing tag now offers `Create "<name>"` inline against the results, which
 * is the specified behaviour and removes the second, competing text field the
 * panel used to carry.
 * @param root0 - The component props
 * @param root0.searchQuery - Current search query for filtering tags
 * @param root0.filteredTags - Tags filtered by search query
 * @param root0.playerTagIds - IDs of tags in the current draft selection
 * @param root0.isCreating - Whether a tag is currently being created
 * @param root0.canCreateNew - Whether a new tag can be created with current search query
 * @param root0.onToggleTag - Callback to toggle a tag in the draft selection
 * @param root0.onCreateFromSearch - Callback to create tag from search query
 */
const TagSelectorAvailableTagsContainer = ({
  searchQuery,
  filteredTags,
  playerTagIds,
  isCreating,
  canCreateNew,
  onToggleTag,
  onCreateFromSearch,
}: TagSelectorAvailableTagsProps) => {
  const playerTagIdsSet = useMemo(() => new Set(playerTagIds), [playerTagIds]);

  return (
    <Stack className="gap-4">
      {/* Available Tags */}
      <Stack className="gap-2">
        <Text variant="caption" className="font-medium text-content-muted">
          {searchQuery ? "SEARCH RESULTS" : "AVAILABLE TAGS"}
        </Text>

        {filteredTags.length === 0 ? (
          <Stack className="items-center justify-center gap-2 py-8">
            <Icon as={Hash} size="xl" className="text-content-subtle" />
            <Text variant="body" className="text-content-muted">
              {searchQuery ? "No tags found" : "No tags available"}
            </Text>
            {canCreateNew && (
              <Button
                label={`Create "${searchQuery.trim()}"`}
                variant="solid"
                tone="accent"
                size="sm"
                icon={Plus}
                iconPosition="leading"
                onPress={onCreateFromSearch}
                accessibilityLabel={`Create tag ${searchQuery.trim()}`}
              />
            )}
          </Stack>
        ) : (
          <ScrollView
            className="max-h-72"
            showsVerticalScrollIndicator={false}
            testID="tag-selector-available-list"
          >
            <Stack className="gap-1">
              {filteredTags.map(item => (
                <TagListItem
                  key={item.id}
                  item={item}
                  isSelected={playerTagIdsSet.has(item.id)}
                  onToggleTag={onToggleTag}
                />
              ))}
            </Stack>
          </ScrollView>
        )}

        {/* Inline create offer: only when the typed name matches no existing
            tag, and only alongside results — the empty state has its own. */}
        {canCreateNew && filteredTags.length > 0 && (
          <Pressable
            onPress={onCreateFromSearch}
            disabled={isCreating}
            accessibilityRole="button"
            accessibilityLabel={`Create tag ${searchQuery.trim()}`}
            testID="tag-selector-create-inline"
            className={`${ROW_BASE_CLASS} border-t border-t-outline-subtle`}
          >
            <Icon
              as={Plus}
              size="xs"
              className="shrink-0 text-accent-primary"
            />
            <Text
              variant="unstyled"
              className="min-w-0 flex-1 truncate text-accent-primary"
            >
              {isCreating
                ? `Creating "${searchQuery.trim()}"...`
                : `Create "${searchQuery.trim()}"`}
            </Text>
          </Pressable>
        )}
      </Stack>
    </Stack>
  );
};

export default TagSelectorAvailableTagsContainer;
